import React, { useCallback, useEffect, useState } from 'react';
import styled from 'styled-components';
import { FiMoreHorizontal as FiMoreHorizontalRaw } from 'react-icons/fi';
import {
  type MonitoringAction,
  type MonitoringActivityType,
  type MonitoringFeedItem,
  type MonitoringStatusFilter,
  superAdminMonitoringService,
} from '../services/superAdminMonitoringService';

const FiMoreHorizontal: any = FiMoreHorizontalRaw;

const Card = styled.div`
  background: var(--surface);
  border: 1px solid var(--outline);
  border-radius: 14px;
  padding: 1.25rem;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.25);
`;

const CardTitle = styled.h2`
  margin: 0 0 0.25rem;
  font-size: 1.2rem;
  color: var(--onSurface);
`;

const CardSubtitle = styled.p`
  margin: 0 0 1rem;
  color: var(--onSurfaceVariant);
`;

const Toolbar = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.75rem;
  align-items: flex-end;
  justify-content: space-between;
  margin-bottom: 1rem;
`;

const ToolbarCluster = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.6rem;
  align-items: flex-end;
`;

const Field = styled.label`
  display: flex;
  flex-direction: column;
  gap: 0.35rem;
  min-width: 140px;
  font-size: 0.82rem;
  color: var(--onSurfaceVariant);
`;

const Input = styled.input`
  border-radius: 10px;
  border: 1px solid var(--outline);
  background: var(--surface);
  color: var(--onSurface);
  padding: 0.55rem 0.7rem;
`;

const Select = styled.select`
  border-radius: 10px;
  border: 1px solid var(--outline);
  background: var(--surface);
  color: var(--onSurface);
  padding: 0.55rem 0.7rem;
`;

const PrimaryButton = styled.button`
  border: 0;
  border-radius: 999px;
  padding: 0.55rem 0.95rem;
  font-weight: 600;
  cursor: pointer;
  background: var(--primary);
  color: var(--onPrimary);

  &:disabled {
    opacity: 0.65;
    cursor: not-allowed;
  }
`;

const GhostButton = styled.button`
  border: 1px solid var(--outline);
  border-radius: 999px;
  padding: 0.5rem 0.85rem;
  font-weight: 600;
  cursor: pointer;
  background: transparent;
  color: var(--onSurface);

  &:disabled {
    opacity: 0.65;
    cursor: not-allowed;
  }
`;

const MenuWrap = styled.div`
  position: relative;
  display: inline-flex;
`;

const MenuButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 2rem;
  height: 2rem;
  border: 1px solid var(--outline);
  border-radius: 10px;
  background: transparent;
  color: var(--onSurface);
  cursor: pointer;
`;

const MenuPanel = styled.div`
  position: absolute;
  top: calc(100% + 0.35rem);
  right: 0;
  min-width: 180px;
  border: 1px solid var(--outline);
  border-radius: 12px;
  background: var(--surface);
  box-shadow: 0 10px 24px rgba(0, 0, 0, 0.25);
  padding: 0.3rem;
  z-index: 20;
`;

const MenuItem = styled.button<{ $danger?: boolean }>`
  width: 100%;
  border: 0;
  border-radius: 8px;
  background: transparent;
  color: ${({ $danger }) => ($danger ? '#ffb4ab' : 'var(--onSurface)')};
  text-align: left;
  padding: 0.5rem 0.65rem;
  font-size: 0.8rem;
  cursor: pointer;

  &:hover {
    background: ${({ $danger }) => ($danger ? 'rgba(244, 67, 54, 0.14)' : 'var(--surfaceVariant)')};
  }

  &:disabled {
    opacity: 0.55;
    cursor: not-allowed;
  }
`;

const Message = styled.div<{ $type: 'error' | 'success' }>`
  border-radius: 12px;
  padding: 0.8rem 0.95rem;
  margin-bottom: 1rem;
  background: ${({ $type }) => ($type === 'error' ? 'rgba(244, 67, 54, 0.14)' : 'rgba(76, 175, 80, 0.14)')};
  color: ${({ $type }) => ($type === 'error' ? '#ffb4ab' : '#b9f6ca')};
`;

const TableShell = styled.div`
  border: 1px solid var(--outline);
  border-radius: 16px;
  overflow: hidden;
`;

const TableScroll = styled.div`
  overflow-x: auto;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;

  th,
  td {
    padding: 0.8rem 0.9rem;
    border-bottom: 1px solid var(--outline);
    text-align: left;
    vertical-align: top;
  }

  th {
    font-size: 0.77rem;
    text-transform: uppercase;
    letter-spacing: 0.04em;
    color: var(--onSurfaceVariant);
    background: color-mix(in srgb, var(--surfaceVariant) 85%, transparent);
  }

  tbody tr:last-child td {
    border-bottom: none;
  }
`;

const Preview = styled.div`
  min-width: 260px;
  max-width: 440px;
  white-space: pre-wrap;
  color: var(--onSurface);
`;

const PreviewStack = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.55rem;
  min-width: 260px;
  max-width: 440px;
`;

const PreviewImageRow = styled.div`
  display: flex;
  gap: 0.45rem;
  flex-wrap: wrap;
`;

const PreviewImage = styled.img`
  width: 72px;
  height: 72px;
  object-fit: cover;
  border-radius: 10px;
  border: 1px solid var(--outline);
  background: var(--surfaceVariant);
`;

const PreviewVideo = styled.video`
  width: min(260px, 100%);
  max-height: 160px;
  border-radius: 12px;
  border: 1px solid var(--outline);
  background: #000;
`;

const MediaBadge = styled.span`
  display: inline-flex;
  align-items: center;
  align-self: flex-start;
  border-radius: 999px;
  padding: 0.18rem 0.5rem;
  font-size: 0.72rem;
  font-weight: 700;
  background: rgba(100, 181, 246, 0.16);
  color: #bbdefb;
`;

const MetaText = styled.div`
  color: var(--onSurfaceVariant);
  font-size: 0.82rem;
`;

const StatusPill = styled.span<{ $tone: 'default' | 'hidden' | 'flagged' | 'deleted' }>`
  display: inline-flex;
  align-items: center;
  border-radius: 999px;
  padding: 0.22rem 0.55rem;
  font-size: 0.74rem;
  font-weight: 700;
  background: ${({ $tone }) => {
    switch ($tone) {
      case 'hidden':
        return 'rgba(255, 183, 77, 0.16)';
      case 'flagged':
        return 'rgba(244, 67, 54, 0.16)';
      case 'deleted':
        return 'rgba(158, 158, 158, 0.2)';
      default:
        return 'rgba(100, 181, 246, 0.16)';
    }
  }};
  color: ${({ $tone }) => {
    switch ($tone) {
      case 'hidden':
        return '#ffd08a';
      case 'flagged':
        return '#ffb4ab';
      case 'deleted':
        return '#e0e0e0';
      default:
        return '#bbdefb';
    }
  }};
`;

const Actions = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.4rem;
`;

const Pager = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 0.75rem;
  margin-top: 1rem;
  flex-wrap: wrap;
`;

const EmptyState = styled.div`
  border: 1px dashed var(--outline);
  border-radius: 16px;
  padding: 1.2rem;
  color: var(--onSurfaceVariant);
  text-align: center;
`;

const activityTypeOptions: Array<{ value: MonitoringActivityType; label: string }> = [
  { value: 'all', label: 'All activity' },
  { value: 'group_post', label: 'Group posts' },
  { value: 'group_post_comment', label: 'Comments & replies' },
  { value: 'group_chat_message', label: 'Group chat' },
  { value: 'direct_message', label: 'Direct messages' },
];

const statusOptions: Array<{ value: MonitoringStatusFilter; label: string }> = [
  { value: 'all', label: 'All states' },
  { value: 'published', label: 'Visible' },
  { value: 'hidden', label: 'Hidden' },
  { value: 'flagged', label: 'Flagged' },
  { value: 'deleted', label: 'Deleted' },
];

const statusToneForItem = (item: MonitoringFeedItem): 'default' | 'hidden' | 'flagged' | 'deleted' => {
  if (item.deleted_at) return 'deleted';
  if (item.hidden_at || item.status === 'hidden') return 'hidden';
  if (item.is_flagged) return 'flagged';
  return 'default';
};

const statusLabelForItem = (item: MonitoringFeedItem): string => {
  if (item.deleted_at) return 'Deleted';
  if (item.hidden_at || item.status === 'hidden') return 'Hidden';
  if (item.is_flagged) return 'Flagged';
  return item.status || 'Visible';
};

const formatDateTime = (value: string | null): string => {
  if (!value) return 'Not available';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleString();
};

const getVisibleImageUrls = (item: MonitoringFeedItem): string[] =>
  Array.isArray(item.image_urls)
    ? item.image_urls.filter((value): value is string => typeof value === 'string' && value.trim().length > 0)
    : [];

const getMediaLabel = (item: MonitoringFeedItem): string | null => {
  if (item.media_kind === 'mixed') return 'Images and video';
  if (item.media_kind === 'video') return 'Video attached';
  if (item.media_kind === 'image' && getVisibleImageUrls(item).length > 0) return 'Image attached';
  return null;
};

export const SuperAdminMonitoringTab: React.FC = () => {
  const [items, setItems] = useState<MonitoringFeedItem[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [message, setMessage] = useState<{ type: 'error' | 'success'; text: string } | null>(null);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(25);
  const [activityType, setActivityType] = useState<MonitoringActivityType>('all');
  const [status, setStatus] = useState<MonitoringStatusFilter>('all');
  const [search, setSearch] = useState('');
  const [groupSearch, setGroupSearch] = useState('');
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [actionItemId, setActionItemId] = useState<string | null>(null);
  const [menuItemId, setMenuItemId] = useState<string | null>(null);

  const loadFeed = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await superAdminMonitoringService.getFeed({
        page,
        pageSize,
        activityType,
        status,
        search,
        groupSearch,
        fromDate,
        toDate,
      });
      setItems(response.items);
      setTotalCount(response.total_count);
    } catch (loadError) {
      setError(loadError instanceof Error ? loadError.message : 'Failed to load monitoring feed.');
      setItems([]);
      setTotalCount(0);
    } finally {
      setLoading(false);
    }
  }, [activityType, fromDate, groupSearch, page, pageSize, search, status, toDate]);

  useEffect(() => {
    void loadFeed();
  }, [loadFeed]);

  useEffect(() => {
    setPage(0);
  }, [activityType, status, search, groupSearch, fromDate, toDate]);

  useEffect(() => {
    setMenuItemId(null);
  }, [items, page, activityType, status, search, groupSearch, fromDate, toDate]);

  const handleAction = useCallback(
    async (item: MonitoringFeedItem, action: MonitoringAction) => {
      setActionItemId(item.item_id);
      setMessage(null);
      try {
        await superAdminMonitoringService.applyAction(item, action);
        setMessage({ type: 'success', text: `${action} applied successfully.` });
        await loadFeed();
      } catch (actionError) {
        setMessage({
          type: 'error',
          text: actionError instanceof Error ? actionError.message : `Failed to ${action} item.`,
        });
      } finally {
        setActionItemId(null);
      }
    },
    [loadFeed]
  );

  const totalPages = Math.max(1, Math.ceil(totalCount / pageSize));

  return (
    <Card>
      <CardTitle>Monitoring</CardTitle>
      <CardSubtitle>
        Unified super admin activity feed for group posts, replies, group chat, and direct messages.
      </CardSubtitle>

      {message ? <Message $type={message.type}>{message.text}</Message> : null}
      {error ? <Message $type="error">{error}</Message> : null}

      <Toolbar>
        <ToolbarCluster>
          <Field>
            Activity type
            <Select value={activityType} onChange={(event) => setActivityType(event.target.value as MonitoringActivityType)}>
              {activityTypeOptions.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </Select>
          </Field>
          <Field>
            State
            <Select value={status} onChange={(event) => setStatus(event.target.value as MonitoringStatusFilter)}>
              {statusOptions.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </Select>
          </Field>
          <Field>
            User search
            <Input value={search} onChange={(event) => setSearch(event.target.value)} placeholder="Name or content" />
          </Field>
          <Field>
            Group search
            <Input value={groupSearch} onChange={(event) => setGroupSearch(event.target.value)} placeholder="Group name" />
          </Field>
          <Field>
            From
            <Input type="date" value={fromDate} onChange={(event) => setFromDate(event.target.value)} />
          </Field>
          <Field>
            To
            <Input type="date" value={toDate} onChange={(event) => setToDate(event.target.value)} />
          </Field>
        </ToolbarCluster>
        <ToolbarCluster>
          <Field>
            Rows per page
            <Select value={String(pageSize)} onChange={(event) => setPageSize(Number(event.target.value))}>
              {[25, 50, 100].map((size) => (
                <option key={size} value={size}>
                  {size}
                </option>
              ))}
            </Select>
          </Field>
          <PrimaryButton disabled={loading} onClick={() => void loadFeed()}>
            {loading ? 'Refreshing' : 'Refresh'}
          </PrimaryButton>
        </ToolbarCluster>
      </Toolbar>

      {items.length === 0 && !loading ? (
        <EmptyState>No monitored activity matches the current filters.</EmptyState>
      ) : (
        <>
          <TableShell>
            <TableScroll>
              <Table>
                <thead>
                  <tr>
                    <th>Type</th>
                    <th>Author</th>
                    <th>Group / Target</th>
                    <th>Content</th>
                    <th>Status</th>
                    <th>Created</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((item) => {
                    const hidden = Boolean(item.hidden_at) || item.status === 'hidden';
                    const flagged = item.is_flagged;
                    const deleted = Boolean(item.deleted_at);
                    const imageUrls = getVisibleImageUrls(item);
                    const mediaLabel = getMediaLabel(item);
                    const menuOpen = menuItemId === item.item_id;
                    return (
                      <tr key={`${item.source_table}:${item.item_id}`}>
                        <td>{item.item_type.replace(/_/g, ' ')}</td>
                        <td>
                          <div>{item.author_name || 'Unknown user'}</div>
                          <MetaText>{item.author_id || 'No author id'}</MetaText>
                        </td>
                        <td>
                          <div>{item.group_name || item.target_user_name || 'Not grouped'}</div>
                          <MetaText>{item.group_id || item.target_user_id || item.related_thread_id || ''}</MetaText>
                        </td>
                        <td>
                          <PreviewStack>
                            {mediaLabel ? <MediaBadge>{mediaLabel}</MediaBadge> : null}
                            {imageUrls.length > 0 ? (
                              <PreviewImageRow>
                                {imageUrls.map((url) => (
                                  <PreviewImage key={url} src={url} alt="Activity attachment" loading="lazy" />
                                ))}
                              </PreviewImageRow>
                            ) : null}
                            {item.video_url ? <PreviewVideo src={item.video_url} controls preload="metadata" /> : null}
                            <Preview>
                              {item.full_content || item.content_preview || 'No content'}
                            </Preview>
                          </PreviewStack>
                        </td>
                        <td>
                          <StatusPill $tone={statusToneForItem(item)}>{statusLabelForItem(item)}</StatusPill>
                        </td>
                        <td>
                          <div>{formatDateTime(item.created_at)}</div>
                          {item.moderated_at ? <MetaText>Moderated {formatDateTime(item.moderated_at)}</MetaText> : null}
                        </td>
                        <td>
                          <MenuWrap>
                            <MenuButton
                              type="button"
                              aria-label="Open activity actions"
                              onClick={() => setMenuItemId(menuOpen ? null : item.item_id)}
                            >
                              <FiMoreHorizontal size={16} />
                            </MenuButton>
                            {menuOpen ? (
                              <MenuPanel>
                                <MenuItem
                                  type="button"
                                  disabled={actionItemId === item.item_id || deleted}
                                  onClick={() => {
                                    setMenuItemId(null);
                                    void handleAction(item, hidden ? 'unhide' : 'hide');
                                  }}
                                >
                                  {hidden ? 'Unhide' : 'Hide'}
                                </MenuItem>
                                <MenuItem
                                  type="button"
                                  disabled={actionItemId === item.item_id || deleted}
                                  onClick={() => {
                                    setMenuItemId(null);
                                    void handleAction(item, flagged ? 'unflag' : 'flag');
                                  }}
                                >
                                  {flagged ? 'Unflag' : 'Flag'}
                                </MenuItem>
                                <MenuItem
                                  type="button"
                                  $danger
                                  disabled={actionItemId === item.item_id || deleted}
                                  onClick={() => {
                                    setMenuItemId(null);
                                    if (window.confirm('Delete this activity item from normal user views?')) {
                                      void handleAction(item, 'delete');
                                    }
                                  }}
                                >
                                  {deleted ? 'Deleted' : 'Delete'}
                                </MenuItem>
                              </MenuPanel>
                            ) : null}
                          </MenuWrap>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </Table>
            </TableScroll>
          </TableShell>

          <Pager>
            <MetaText>
              Page {Math.min(page + 1, totalPages)} of {totalPages} · {totalCount} rows
            </MetaText>
            <Actions>
              <GhostButton disabled={page === 0 || loading} onClick={() => setPage((current) => Math.max(0, current - 1))}>
                Previous
              </GhostButton>
              <GhostButton
                disabled={loading || page + 1 >= totalPages}
                onClick={() => setPage((current) => current + 1)}
              >
                Next
              </GhostButton>
            </Actions>
          </Pager>
        </>
      )}
    </Card>
  );
};

export default SuperAdminMonitoringTab;